/*
 * Dynamic-list rule builder (BULK_EMAIL_MANAGEMENT §7.1 field catalog).
 *
 * Rule JSON contract (AudienceRuleCompiler):
 *   group = { op: "and"|"or"|"not", rules: [...] }
 *   leaf  = { field, op, value, days? }        (product_viewed may carry productIds[])
 * The catalog below mirrors AudienceRuleCompiler.Catalog; the server is the
 * authority: a 400 from rules/estimate or create carries `fields` and the
 * message; the builder shows the message.
 *
 * List ids (in_list / not_in_list) are 19-digit integers: they are kept as
 * STRINGS in the rule value array (the compiler parses them with long.TryParse).
 *
 * Estimate (2026-10-04): live, 600 ms after the last edit: POST /rules/estimate
 * {ruleJson} → {count, sample?[]}; a host can pass its own `estimator(ruleJson)`
 * (the "From a filter" dialog uses members/from-rule preview, which also
 * reports alreadyMembers / wouldAdd).
 */
import React from "react";
import { Link } from "react-router-dom";
import { num, usd } from "../sales/territory-common";
import { previewRule } from "./api";
const NUM_OPS = [["eq", "is"], ["neq", "is not"], ["gt", "is more than"], ["gte", "is at least"], ["lt", "is less than"], ["lte", "is at most"], ["between", "is between"]];
const SET_OPS = [["in", "is any of"], ["not_in", "is none of"]];
const BOOL_OPS = [["eq", "is"], ["neq", "is not"]];
const WITHIN_OPS = [["within", "in the last"], ["not_within", "not in the last"]];
const ENGAGE_OPS = [["opened_within", "opened one in the last"], ["clicked_within", "clicked one in the last"], ["not_opened_within", "got one but did not open it in the last"]];
const ENGAGE_TEXT = {
  opened_within: "opened an email",
  clicked_within: "clicked an email",
  not_opened_within: "got an email but did not open it"
};

/*
 * Field catalog. `f` is the AudienceRuleCompiler key (never shown); `label` is what people read in the picker.
 * Sentence pieces for describeRule: `verb`/`notVerb` for date fields, `yes`/`no` for yes-or-no fields,
 * `names` maps stored values to readable ones.
 */
export const FIELDS = [{
  f: "customer_type",
  label: "Kind of customer",
  kind: "set",
  options: ["individual", "business"],
  names: {
    individual: "Individual buyer",
    business: "Business buyer"
  }
}, {
  f: "lifecycle_stage",
  label: "Stage",
  kind: "set",
  options: ["lead", "prospect", "customer", "none"],
  names: {
    lead: "Lead",
    prospect: "Prospect",
    customer: "Customer",
    none: "Not set"
  }
}, {
  f: "site_visit",
  label: "Visited your site",
  kind: "days",
  verb: "visited your site",
  notVerb: "have not visited your site"
}, {
  f: "product_viewed",
  label: "Looked at a product",
  kind: "days",
  verb: "looked at a product",
  notVerb: "have not looked at a product"
}, {
  f: "email_engagement",
  label: "Email activity",
  kind: "engage"
}, {
  f: "email_opt_in",
  label: "Agreed to get email",
  kind: "bool",
  yes: "agreed to get email",
  no: "have not agreed to get email"
}, {
  f: "lapsed",
  label: "Stopped buying",
  kind: "bool",
  yes: "have stopped buying",
  no: "are still buying"
}, {
  f: "last_order_days",
  label: "Days since last order",
  kind: "num"
}, {
  f: "order_count",
  label: "Number of orders",
  kind: "num"
}, {
  f: "lifetime_value",
  label: "Total spent ($)",
  kind: "num",
  money: true
}, {
  f: "category_purchased",
  label: "Bought from category",
  kind: "set",
  free: true,
  optDays: true,
  extraOps: [["contains", "contains"], ["not_contains", "does not contain"]]
}, {
  f: "product_purchased",
  label: "Bought product (product ids)",
  kind: "set",
  free: true,
  optDays: true
}, {
  f: "in_list",
  label: "Is in list",
  kind: "list"
}, {
  f: "not_in_list",
  label: "Is not in list",
  kind: "list"
}, {
  f: "country",
  label: "Country",
  kind: "set",
  free: true
}, {
  f: "region",
  label: "State or region",
  kind: "set",
  free: true
}, {
  f: "postal",
  label: "Postal code",
  kind: "set",
  free: true,
  extraOps: [["starts_with", "starts with"]]
}, {
  f: "territory",
  label: "Sales territory",
  kind: "set",
  free: true
}, {
  f: "rep",
  label: "Sales rep",
  kind: "set",
  free: true
}, {
  f: "tag",
  label: "Tag",
  kind: "set",
  free: true
}, {
  f: "source",
  label: "Came from",
  kind: "set",
  free: true,
  extraOps: [["contains", "contains"], ["not_contains", "does not contain"]]
}, {
  f: "email_domain",
  label: "Email domain",
  kind: "set",
  free: true
}, {
  f: "created_days",
  label: "Added to your contacts",
  kind: "days",
  verb: "were added to your contacts",
  notVerb: "were not added to your contacts"
}];
const fieldDef = f => FIELDS.find(x => x.f === f) || FIELDS[0];
const valName = (d, v) => d.names && d.names[v] || String(v);
export function emptyLeaf(field = "customer_type") {
  const d = fieldDef(field);
  switch (d.kind) {
    case "num":
      return {
        field,
        op: "gte",
        value: 1
      };
    case "bool":
      return {
        field,
        op: "eq",
        value: true
      };
    case "days":
      return {
        field,
        op: "within",
        days: 30
      };
    case "engage":
      return {
        field,
        op: "opened_within",
        days: 90
      };
    case "list":
      return {
        field,
        op: "in",
        value: []
      };
    default:
      return {
        field,
        op: "in",
        value: d.options ? [d.options[0]] : []
      };
  }
}
export const emptyGroup = () => ({
  op: "and",
  rules: [emptyLeaf()]
});

/** Parse the API's rule_json (string or object) into the builder's tree; falls back to an empty group. */
export function parseRule(ruleJson) {
  if (!ruleJson) return emptyGroup();
  try {
    const o = typeof ruleJson === "string" ? JSON.parse(ruleJson) : ruleJson;
    if (o && o.field) return {
      op: "and",
      rules: [o]
    };
    if (o && Array.isArray(o.rules)) return o;
  } catch (e) {/* fall through */}
  return emptyGroup();
}
const orList = (xs, word = "or") => xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} ${word} ${xs[xs.length - 1]}`;

/** One condition as a clause that reads after "People who" (verb clauses) or "People whose" (clauses starting "whose"). */
function leafText(n, lists) {
  const d = fieldDef(n.field);
  const vals = Array.isArray(n.value) ? n.value : n.value == null ? [] : [n.value];
  const days = n.days != null && n.days !== "" ? Number(n.days) : null;
  const inDays = x => `in the last ${num(x)} ${Number(x) === 1 ? "day" : "days"}`;
  switch (d.kind) {
    case "days":
      return n.op === "not_within" ? `${d.notVerb} ${inDays(days)}` : `${d.verb} ${inDays(days)}`;
    case "engage":
      return `${ENGAGE_TEXT[n.op] || "opened an email"} ${inDays(days)}`;
    case "bool":
      {
        const yes = n.op === "neq" !== (n.value === false || n.value === "false");
        return yes ? d.no : d.yes;
      }
    case "list":
      {
        const names = vals.map(id => {
          const l = (lists || []).find(x => String(x.id) === String(id));
          return l ? `“${l.name}”` : "another list";
        });
        return `${n.field === "not_in_list" ? "are not in" : "are in"} ${orList(names)}`;
      }
    case "num":
      {
        const f = x => d.money ? usd(x) : num(x);
        const op = (NUM_OPS.find(([v]) => v === n.op) || NUM_OPS[0])[1];
        const v = n.op === "between" ? `${f(vals[0])} and ${f(vals[1])}` : f(vals[0]);
        return `whose ${d.label.replace(/ \(\$\)$/, "").toLowerCase()} ${op} ${v}`;
      }
    default:
      {
        const shown = vals.map(v => valName(d, v));
        const label = d.label.replace(/ \(.*\)$/, "").toLowerCase();
        const base = n.op === "not_in" ? shown.length > 1 ? `is none of ${orList(shown)}` : `is not ${shown[0] || ""}` : n.op === "in" ? shown.length > 1 ? `is ${orList(shown)}` : `is ${shown[0] || ""}` : `${String(n.op).replace(/_/g, " ")} ${orList(shown)}`;
        let clause;
        if (n.field === "category_purchased" || n.field === "product_purchased") {
          const what = n.field === "category_purchased" ? "from" : "";
          clause = n.op === "not_in" ? `have not bought ${what} ${orList(shown)}` : n.op === "in" ? `bought ${what} ${orList(shown)}` : `bought from a category that ${base}`;
          clause = clause.replace(/  +/g, " ");
          return days ? `${clause} ${inDays(days)}` : clause;
        }
        clause = `whose ${label} ${base}`;
        return days ? `${clause} ${inDays(days)}` : clause;
      }
  }
}

/** Plain sentence for a rule tree, for example "People whose kind of customer is Business buyer and who visited your site in the last 90 days." */
export function describeRule(ruleJson, lists) {
  const join = (clauses, op) => {
    const word = op === "or" ? "or" : "and";
    const leads = clauses.map(c => c.startsWith("whose ") || c.startsWith("(") ? c : `who ${c}`);
    if (leads.length <= 1) return leads.join("");
    if (leads.length === 2) return `${leads[0]} ${word} ${leads[1]}`;
    return `${leads.slice(0, -1).join(", ")}, ${word} ${leads[leads.length - 1]}`;
  };
  const node = (n, top) => {
    if (!n) return "";
    if (n.field) return leafText(n, lists);
    const parts = (n.rules || []).map(r => node(r, false)).filter(Boolean);
    if (!parts.length) return "";
    const body = join(parts, n.op);
    if (n.op === "not") return `(not matching: ${body})`;
    return top || parts.length === 1 ? body : `(${body})`;
  };
  const s = node(parseRule(ruleJson), true);
  if (!s) return "";
  return `People ${s}.`;
}

/** Validates client-side; returns a plain message or null. */
export function validateRule(tree) {
  let leaves = 0,
    err = null;
  const walk = n => {
    if (err) return;
    if (n.field) {
      leaves++;
      const d = fieldDef(n.field);
      if ((d.kind === "days" || d.kind === "engage") && !(Number(n.days) > 0)) err = `Fill in the number of days for “${d.label}”.`;
      if (d.kind === "num" && n.op === "between" && !(Array.isArray(n.value) && n.value.length === 2)) err = `“${d.label}” needs a low and a high number.`;
      if (d.kind === "num" && n.op !== "between" && (n.value === "" || n.value == null || isNaN(Number(n.value)))) err = `Fill in a number for “${d.label}”.`;
      if ((d.kind === "set" || d.kind === "list") && (!Array.isArray(n.value) || n.value.length === 0)) err = `Pick at least one choice for “${d.label}”.`;
      return;
    }
    if (!n.rules || !n.rules.length) {
      err = "Each group needs at least one line.";
      return;
    }
    n.rules.forEach(walk);
  };
  walk(tree);
  if (!err && leaves === 0) err = "Add at least one line.";
  return err;
}
function Leaf({
  leaf,
  lists,
  onChange,
  onRemove
}) {
  const d = fieldDef(leaf.field);
  const set = patch => onChange({
    ...leaf,
    ...patch
  });
  const ops = d.kind === "num" ? NUM_OPS : d.kind === "bool" ? BOOL_OPS : d.kind === "days" ? WITHIN_OPS : d.kind === "engage" ? ENGAGE_OPS : [...SET_OPS, ...(d.extraOps || [])];
  const valuesText = Array.isArray(leaf.value) ? leaf.value.join(", ") : leaf.value == null ? "" : String(leaf.value);
  // Toggle chips instead of a native multi-select: the listbox grew taller than the row and broke alignment.
  const multi = options => {
    const picked = (leaf.value || []).map(String);
    const toggle = v => set({
      value: picked.includes(v) ? picked.filter(x => x !== v) : [...picked, v]
    });
    return /*#__PURE__*/React.createElement("span", {
      className: "ane-multi",
      role: "group"
    }, options.map(o => {
      const v = String(o.v);
      const on = picked.includes(v);
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        key: v,
        className: "ane-chip" + (on ? " on" : ""),
        "aria-pressed": on,
        onClick: () => toggle(v)
      }, o.l);
    }), !options.length ? /*#__PURE__*/React.createElement("span", {
      className: "ane-muted"
    }, "No lists to pick yet.") : null);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "ane-row"
  }, /*#__PURE__*/React.createElement("select", {
    className: "ane-sel ane-field",
    "aria-label": "What to check",
    value: leaf.field,
    onChange: e => onChange(emptyLeaf(e.target.value))
  }, FIELDS.map(x => /*#__PURE__*/React.createElement("option", {
    key: x.f,
    value: x.f
  }, x.label))), /*#__PURE__*/React.createElement("select", {
    className: "ane-sel ane-op",
    "aria-label": "How to compare",
    value: leaf.op,
    onChange: e => set({
      op: e.target.value,
      value: e.target.value === "between" ? [0, 0] : leaf.op === "between" ? 0 : leaf.value
    })
  }, ops.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
    key: v,
    value: v
  }, l))), /*#__PURE__*/React.createElement("span", {
    className: "ane-val"
  }, d.kind === "num" && leaf.op === "between" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
    type: "number",
    className: "ane-in ane-num",
    "aria-label": "From",
    value: (leaf.value || [])[0] ?? "",
    onChange: e => set({
      value: [e.target.value, (leaf.value || [])[1] ?? ""]
    })
  }), /*#__PURE__*/React.createElement("span", {
    className: "ane-word"
  }, "and"), /*#__PURE__*/React.createElement("input", {
    type: "number",
    className: "ane-in ane-num",
    "aria-label": "To",
    value: (leaf.value || [])[1] ?? "",
    onChange: e => set({
      value: [(leaf.value || [])[0] ?? "", e.target.value]
    })
  })), d.kind === "num" && leaf.op !== "between" && /*#__PURE__*/React.createElement("input", {
    type: "number",
    className: "ane-in ane-num",
    "aria-label": "Number",
    value: leaf.value ?? "",
    onChange: e => set({
      value: e.target.value
    })
  }), d.kind === "bool" && /*#__PURE__*/React.createElement("select", {
    className: "ane-sel",
    "aria-label": "Yes or no",
    value: String(leaf.value),
    onChange: e => set({
      value: e.target.value === "true"
    })
  }, /*#__PURE__*/React.createElement("option", {
    value: "true"
  }, "yes"), /*#__PURE__*/React.createElement("option", {
    value: "false"
  }, "no")), (d.kind === "days" || d.kind === "engage") && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
    type: "number",
    min: "1",
    max: "3650",
    className: "ane-in ane-num",
    "aria-label": "Days",
    value: leaf.days ?? "",
    onChange: e => set({
      days: e.target.value
    })
  }), /*#__PURE__*/React.createElement("span", {
    className: "ane-word"
  }, "days")), d.kind === "list" && multi((lists || []).map(l => ({
    v: String(l.id),
    l: l.name
  }))), d.kind === "set" && d.options && !d.free && multi(d.options.map(o => ({
    v: o,
    l: valName(d, o)
  }))), d.kind === "set" && d.free && /*#__PURE__*/React.createElement("input", {
    type: "text",
    className: "ane-in ane-text",
    "aria-label": "Values",
    placeholder: "separate several with commas",
    value: valuesText,
    onChange: e => set({
      value: e.target.value.split(",").map(x => x.trim()).filter(Boolean)
    })
  }), d.optDays && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "ane-word"
  }, "in the last"), /*#__PURE__*/React.createElement("input", {
    type: "number",
    min: "1",
    className: "ane-in ane-num",
    "aria-label": "Days (optional)",
    placeholder: "any",
    value: leaf.days ?? "",
    onChange: e => set({
      days: e.target.value === "" ? undefined : e.target.value
    })
  }), /*#__PURE__*/React.createElement("span", {
    className: "ane-word"
  }, "days"))), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "ane-x",
    "aria-label": "Remove this line",
    title: "Remove",
    onClick: onRemove
  }, "\u2715"));
}
const MatchSelect = ({
  value,
  onChange
}) => /*#__PURE__*/React.createElement("select", {
  className: "ane-sel ane-match",
  "aria-label": "All or any",
  value: value === "or" ? "or" : "and",
  onChange: e => onChange(e.target.value)
}, /*#__PURE__*/React.createElement("option", {
  value: "and"
}, "all"), /*#__PURE__*/React.createElement("option", {
  value: "or"
}, "any"));
function Group({
  group,
  lists,
  onChange,
  onRemove,
  depth
}) {
  const set = patch => onChange({
    ...group,
    ...patch
  });
  const rules = group.rules || [];
  const replace = (i, r) => set({
    rules: rules.map((x, j) => j === i ? r : x)
  });
  const remove = i => set({
    rules: rules.filter((_, j) => j !== i)
  });
  return /*#__PURE__*/React.createElement("div", {
    className: `ane-group d${depth}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "ane-group-head"
  }, /*#__PURE__*/React.createElement("span", null, depth === 0 ? "People who match" : "Or a group: match"), /*#__PURE__*/React.createElement(MatchSelect, {
    value: group.op,
    onChange: v => set({
      op: v
    })
  }), /*#__PURE__*/React.createElement("span", null, "of these:"), onRemove ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "ane-x ane-right",
    "aria-label": "Remove this group",
    title: "Remove group",
    onClick: onRemove
  }, "\u2715") : null), /*#__PURE__*/React.createElement("div", {
    className: "ane-group-body"
  }, rules.map((r, i) => r.field ? /*#__PURE__*/React.createElement(Leaf, {
    key: i,
    leaf: r,
    lists: lists,
    onChange: x => replace(i, x),
    onRemove: () => remove(i)
  }) : /*#__PURE__*/React.createElement(Group, {
    key: i,
    group: r,
    lists: lists,
    depth: depth + 1,
    onChange: x => replace(i, x),
    onRemove: () => remove(i)
  })), rules.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: "ane-muted"
  }, "Nothing here yet. Press Add another.") : null), /*#__PURE__*/React.createElement("div", {
    className: "ane-group-foot"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "ane-add",
    onClick: () => set({
      rules: [...rules, emptyLeaf()]
    })
  }, "+ Add another"), depth < 2 ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "ane-addgroup",
    onClick: () => set({
      rules: [...rules, {
        op: group.op === "and" ? "or" : "and",
        rules: [emptyLeaf()]
      }]
    })
  }, "+ Add a group") : null));
}

/** Serialize the builder tree to the API shape (numbers coerced; list ids stay strings). */
export function toRuleJson(tree) {
  const clean = n => {
    if (n.field) {
      const d = fieldDef(n.field);
      const out = {
        field: n.field,
        op: n.op
      };
      if (d.kind === "num") out.value = n.op === "between" ? (n.value || []).map(v => Number(v)) : Number(n.value);else if (d.kind === "bool") out.value = !!n.value;else if (d.kind === "days" || d.kind === "engage") out.days = Number(n.days);else out.value = Array.isArray(n.value) ? n.value.map(String) : [String(n.value)];
      if (d.optDays && n.days !== undefined && n.days !== "" && n.days !== null) out.days = Number(n.days);
      return out;
    }
    return {
      op: n.op || "and",
      rules: (n.rules || []).map(clean)
    };
  };
  return clean(tree);
}

/** Sample of matching people (from the estimate or preview): [{personId, firstName, lastName, email, company?}] */
export function SampleList({
  sample,
  total,
  max = 3
}) {
  const rows = (sample || []).slice(0, max);
  if (!rows.length) return null;
  const more = Number(total) - rows.length;
  return /*#__PURE__*/React.createElement("ul", {
    className: "ane-sample"
  }, rows.map((p, i) => {
    const id = p.personId != null ? String(p.personId) : p.id != null ? String(p.id) : null;
    const name = [p.firstName, p.lastName].filter(Boolean).join(" ") || p.name || p.email || "(no name)";
    const extra = p.company || (p.email && p.email !== name ? p.email : "");
    return /*#__PURE__*/React.createElement("li", {
      key: id || i
    }, id ? /*#__PURE__*/React.createElement(Link, {
      to: `/Contacts/Detail/${encodeURIComponent(id)}`
    }, name) : name, extra ? /*#__PURE__*/React.createElement("span", {
      className: "ane-muted"
    }, " \xB7 ", extra) : null);
  }), more > 0 ? /*#__PURE__*/React.createElement("li", {
    className: "ane-muted"
  }, "and ", num(more), " more") : null);
}

/** Live count box: "Right now this matches N people". */
function CountBox({
  estimate,
  busy,
  clientErr
}) {
  const emailable = estimate && (estimate.withEmail != null ? estimate.withEmail : estimate.emailable != null ? estimate.emailable : estimate.canEmail);
  return /*#__PURE__*/React.createElement("section", {
    className: `ane-count${busy ? " pending" : ""}`,
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement("span", {
    className: "ane-muted"
  }, "Right now this matches"), clientErr ? /*#__PURE__*/React.createElement("span", {
    className: "ane-count-help"
  }, clientErr) : estimate && estimate.error ? /*#__PURE__*/React.createElement("span", {
    className: "ane-count-err"
  }, estimate.error) : estimate ? /*#__PURE__*/React.createElement("b", {
    className: "ane-count-n"
  }, num(estimate.count), " ", estimate.count === 1 ? "person" : "people") : /*#__PURE__*/React.createElement("b", {
    className: "ane-count-n ane-dim"
  }, busy ? "Counting…" : "…"), estimate && !estimate.error && estimate.wouldAdd != null ? /*#__PURE__*/React.createElement("span", {
    className: "ane-count-help"
  }, num(estimate.alreadyMembers), " already here, ", /*#__PURE__*/React.createElement("b", null, num(estimate.wouldAdd)), " would be added.") : null, !clientErr && !(estimate && estimate.error) ? /*#__PURE__*/React.createElement("span", {
    className: "ane-count-help"
  }, emailable != null ? `${num(emailable)} can get email. ` : "", "Updates as you edit.") : null, estimate && !estimate.error && estimate.sample && estimate.sample.length ? /*#__PURE__*/React.createElement(SampleList, {
    sample: estimate.sample,
    total: estimate.count
  }) : null);
}

/**
 * <RuleBuilder value={tree} onChange={tree} lists={[{id,name}]} estimator? onEstimate? />
 * Rows that read as a sentence on the left, a live count box on the right (stacks below on narrow screens).
 * The count runs 600 ms after the last edit. estimator(ruleJson) → {count|matched, sample?[], alreadyMembers?, wouldAdd?};
 * defaults to POST /rules/estimate.
 */
export default function RuleBuilder({
  value,
  onChange,
  lists,
  estimator,
  onEstimate,
  aside
}) {
  const [estimate, setEstimate] = React.useState(null); // {count, sample, ...} | {error}
  const [busy, setBusy] = React.useState(false);
  const tree = value || emptyGroup();
  const clientErr = validateRule(tree);
  const key = JSON.stringify(tree);
  const seq = React.useRef(0);
  React.useEffect(() => {
    if (clientErr) {
      setEstimate(null);
      setBusy(false);
      onEstimate && onEstimate(null);
      return undefined;
    }
    const my = ++seq.current;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const ruleJson = toRuleJson(tree);
        const r = await (estimator ? estimator(ruleJson) : previewRule(ruleJson));
        if (my !== seq.current) return;
        const out = {
          ...(r || {}),
          count: Number(r && (r.count != null ? r.count : r.matched)) || 0,
          sample: r && r.sample || []
        };
        setEstimate(out);
        onEstimate && onEstimate(out);
      } catch (err) {
        if (my !== seq.current) return;
        setEstimate({
          error: err.message || "We could not count this right now."
        });
        onEstimate && onEstimate(null);
      } finally {
        if (my === seq.current) setBusy(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return /*#__PURE__*/React.createElement("div", {
    className: "ane-rules"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ane-rules-main"
  }, /*#__PURE__*/React.createElement(Group, {
    group: tree,
    lists: lists,
    depth: 0,
    onChange: onChange
  })), /*#__PURE__*/React.createElement("div", {
    className: "ane-rules-side"
  }, /*#__PURE__*/React.createElement(CountBox, {
    estimate: estimate,
    busy: busy,
    clientErr: clientErr
  }), aside || null));
}